import { useState, useEffect } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { AlertTriangle, Plus, Search, Filter, Radar, ChevronDown, Check, Play, RefreshCw, Clock, Calendar, Server, Layers, Eye, Globe, Mail, XCircle, Database, Building2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function Threats() {
  const { isDarkMode } = useTheme();
  const [threats, setThreats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // list or by-asset
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isScanDialogOpen, setIsScanDialogOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [newThreat, setNewThreat] = useState({
    title: '',
    description: '',
    severity: 'medium',
    source: 'internal',
  });
  const [scanConfig, setScanConfig] = useState({
    scan_type: 'full',
  });
  const [assets, setAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);

  useEffect(() => {
    fetchThreats();
    fetchAssets(); // Fetch assets for grouping view
  }, []);

  const fetchThreats = async () => {
    try {
      const response = await api.get('/threats');
      setThreats(response.data);
    } catch (error) {
      toast.error('Failed to fetch threats');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssets = async () => {
    setLoadingAssets(true);
    try {
      const response = await api.get('/assets');
      setAssets(response.data);
    } catch (error) {
      toast.error('Failed to fetch assets');
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleScanDialogOpen = (open) => {
    setIsScanDialogOpen(open);
    if (open && scanConfig.scan_type === 'custom' && assets.length === 0) {
      fetchAssets();
    }
    if (!open) {
      setSelectedAssets([]);
    }
  };

  const handleScanTypeChange = (value) => {
    setScanConfig({ ...scanConfig, scan_type: value });
    if (value === 'custom' && assets.length === 0) {
      fetchAssets();
    }
    if (value !== 'custom') {
      setSelectedAssets([]);
    }
  };

  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId)
        : [...prev, assetId]
    );
  };

  const selectAllAssets = () => {
    if (selectedAssets.length === assets.length) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets(assets.map(a => a.id));
    }
  };

  const handleCreateThreat = async (e) => {
    e.preventDefault();
    try {
      await api.post('/threats', newThreat);
      toast.success('Threat created successfully');
      setIsDialogOpen(false);
      setNewThreat({ title: '', description: '', severity: 'medium', source: 'internal' });
      fetchThreats();
    } catch (error) {
      toast.error('Failed to create threat');
    }
  };

  const handleStatusChange = async (threatId, newStatus) => {
    setUpdatingStatus(threatId);
    try {
      await api.patch(`/threats/${threatId}/status`, { status: newStatus });
      setThreats(prev => 
        prev.map(t => t.id === threatId ? { ...t, status: newStatus } : t)
      );
      toast.success(`Threat status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleRunScan = async () => {
    setScanning(true);
    try {
      const payload = {
        scan_type: scanConfig.scan_type,
        targets: scanConfig.scan_type === 'custom' ? selectedAssets : null
      };
      const response = await api.post('/threats/scan', payload);
      toast.success(response.data.message);
      setIsScanDialogOpen(false);
      setSelectedAssets([]);
      fetchThreats();
    } catch (error) {
      toast.error('Failed to run threat scan');
    } finally {
      setScanning(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-green-500/20 text-green-400 border-green-500/30',
      info: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return colors[severity] || colors.info;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'text-red-400',
      inactive: 'text-zinc-400',
      investigating: 'text-yellow-400',
      mitigated: 'text-green-400',
    };
    return colors[status] || 'text-zinc-400';
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Active',
      inactive: 'Inactive',
      investigating: 'Investigating',
      mitigated: 'Mitigated',
    };
    return labels[status] || status;
  };

  const filteredThreats = threats.filter((threat) => {
    const matchesSearch = threat.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         threat.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = severityFilter === 'all' || threat.severity === severityFilter;
    const matchesStatus = statusFilter === 'all' || threat.status === statusFilter;
    return matchesSearch && matchesSeverity && matchesStatus;
  });

  // Group threats by asset
  const threatsByAsset = assets.reduce((acc, asset) => {
    const assetThreats = filteredThreats.filter(t => 
      t.affected_assets?.includes(asset.id) || 
      t.asset_id === asset.id ||
      t.target?.includes(asset.name) ||
      t.target?.includes(asset.hostname)
    );
    if (assetThreats.length > 0) {
      acc[asset.id] = {
        asset,
        threats: assetThreats
      };
    }
    return acc;
  }, {});
  
  // Get unassigned threats (not linked to any asset)
  const unassignedThreats = filteredThreats.filter(t => 
    !t.affected_assets?.length && !t.asset_id
  );

  const stats = {
    total: threats.length,
    active: threats.filter(t => t.status === 'active').length,
    investigating: threats.filter(t => t.status === 'investigating').length,
    mitigated: threats.filter(t => t.status === 'mitigated').length,
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-96 ${isDarkMode ? '' : ''}`}>
        <div className={`font-rajdhani text-xl ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>Loading threats...</div>
      </div>
    );
  }

  return (
    <div data-testid="threats-container" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`font-rajdhani font-bold text-4xl ${isDarkMode ? 'text-white text-shadow-neon' : 'text-slate-900'}`}>
            Threat Management
          </h1>
          <p className={`font-inter mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>Monitor and manage security threats</p>
        </div>

        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className={`flex items-center rounded-lg border p-1 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200'}`}>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-indigo-600' : ''}
            >
              <Filter className="w-4 h-4 mr-1" />
              List
            </Button>
            <Button
              variant={viewMode === 'by-asset' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('by-asset')}
              className={viewMode === 'by-asset' ? 'bg-indigo-600' : ''}
            >
              <Layers className="w-4 h-4 mr-1" />
              By Asset
            </Button>
          </div>
          
          {/* Scan for Threats Button */}
          <Dialog open={isScanDialogOpen} onOpenChange={handleScanDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="scan-threats-button" 
                className="bg-violet-600 hover:bg-violet-500 font-rajdhani"
              >
                <Radar className="w-4 h-4 mr-2" />
                Scan for Threats
              </Button>
            </DialogTrigger>
            <DialogContent className={`max-w-lg ${isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200'}`}>
              <DialogHeader>
                <DialogTitle className={`font-rajdhani text-2xl flex items-center gap-2 ${isDarkMode ? '' : 'text-slate-900'}`}>
                  <Radar className={`w-6 h-6 ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                  Run Threat Scan
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
                  Run an AI-powered scan to detect potential security threats across your assets.
                </p>
                <div>
                  <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Scan Type</Label>
                  <Select 
                    value={scanConfig.scan_type} 
                    onValueChange={handleScanTypeChange}
                  >
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200'}>
                      <SelectItem value="full">Full Scan (All Assets)</SelectItem>
                      <SelectItem value="quick">Quick Scan (Critical Assets)</SelectItem>
                      <SelectItem value="custom">Custom Scan (Select Assets)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Asset Selection */}
                {scanConfig.scan_type === 'custom' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className={isDarkMode ? 'text-zinc-300' : 'text-slate-700'}>Select Assets to Scan</Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={selectAllAssets}
                        className={`text-xs ${isDarkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-500'}`}
                      >
                        {selectedAssets.length === assets.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                    <div className={`max-h-48 overflow-y-auto space-y-2 p-2 rounded-lg border ${isDarkMode ? 'bg-zinc-950/50 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                      {loadingAssets ? (
                        <div className="flex items-center justify-center py-4">
                          <RefreshCw className={`w-5 h-5 animate-spin ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                        </div>
                      ) : assets.length === 0 ? (
                        <p className="text-zinc-500 text-sm text-center py-4">No assets found</p>
                      ) : (
                        assets.map((asset) => (
                          <div
                            key={asset.id}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                              selectedAssets.includes(asset.id) ? 'bg-violet-600/20' : 'hover:bg-white/5'
                            }`}
                            onClick={() => toggleAssetSelection(asset.id)}
                          >
                            <Checkbox
                              checked={selectedAssets.includes(asset.id)}
                              onCheckedChange={() => toggleAssetSelection(asset.id)}
                              className="border-white/30 data-[state=checked]:bg-violet-600"
                            />
                            <Server className="w-4 h-4 text-zinc-400" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{asset.name}</p>
                              <p className="text-xs text-zinc-500">{asset.type} • {asset.ip_address || asset.url || 'No address'}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {selectedAssets.length > 0 && (
                      <p className="text-xs text-indigo-400">
                        {selectedAssets.length} asset{selectedAssets.length !== 1 ? 's' : ''} selected
                      </p>
                    )}
                  </div>
                )}

                <div className="p-4 bg-violet-600/10 border border-violet-500/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Radar className="w-4 h-4 text-violet-400 animate-pulse" />
                    <span className="text-violet-400 font-rajdhani font-semibold">AI-Powered Detection</span>
                  </div>
                  <p className="text-zinc-400 text-xs">
                    Uses advanced AI to analyze asset configurations, network patterns, and behavioral anomalies.
                  </p>
                </div>
                <Button
                  data-testid="run-scan-button"
                  onClick={handleRunScan}
                  disabled={scanning || (scanConfig.scan_type === 'custom' && selectedAssets.length === 0)}
                  className="w-full bg-violet-600 hover:bg-violet-500"
                >
                  {scanning ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Scanning...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Start Scan
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Threat Button */}
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-threat-button" className="bg-indigo-600 hover:bg-indigo-500 font-rajdhani shadow-neon-indigo">
                <Plus className="w-4 h-4 mr-2" />
                Add Threat
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-2xl">Add New Threat</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateThreat} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="title" className="text-zinc-300">Title</Label>
                  <Input
                    id="title"
                    data-testid="threat-title-input"
                    value={newThreat.title}
                    onChange={(e) => setNewThreat({ ...newThreat, title: e.target.value })}
                    className="bg-zinc-950/50 border-white/10 text-white mt-2"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description" className="text-zinc-300">Description</Label>
                  <textarea
                    id="description"
                    data-testid="threat-description-input"
                    value={newThreat.description}
                    onChange={(e) => setNewThreat({ ...newThreat, description: e.target.value })}
                    className="w-full bg-zinc-950/50 border border-white/10 rounded-md p-3 text-white mt-2 min-h-[100px]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="severity" className="text-zinc-300">Severity</Label>
                  <Select value={newThreat.severity} onValueChange={(value) => setNewThreat({ ...newThreat, severity: value })}>
                    <SelectTrigger data-testid="threat-severity-select" className="bg-zinc-950/50 border-white/10 text-white mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10 text-white">
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="info">Info</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="source" className="text-zinc-300">Source</Label>
                  <Input
                    id="source"
                    data-testid="threat-source-input"
                    value={newThreat.source}
                    onChange={(e) => setNewThreat({ ...newThreat, source: e.target.value })}
                    className="bg-zinc-950/50 border-white/10 text-white mt-2"
                    required
                  />
                </div>
                <Button data-testid="submit-threat-button" type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500">
                  Create Threat
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={`rounded-xl p-4 text-center ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className={`text-2xl font-rajdhani font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{stats.total}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Total Threats</p>
        </div>
        <div className={`rounded-xl p-4 text-center border-l-2 border-red-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className={`text-2xl font-rajdhani font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{stats.active}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Active</p>
        </div>
        <div className={`rounded-xl p-4 text-center border-l-2 border-yellow-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className={`text-2xl font-rajdhani font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{stats.investigating}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Investigating</p>
        </div>
        <div className={`rounded-xl p-4 text-center border-l-2 border-green-500 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <p className={`text-2xl font-rajdhani font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>{stats.mitigated}</p>
          <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Mitigated</p>
        </div>
      </div>

      <div className={`rounded-2xl p-6 ${isDarkMode ? 'glass' : 'bg-white border border-slate-200 shadow-sm'}`}>
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-zinc-500' : 'text-slate-400'}`} />
            <Input
              data-testid="search-threats-input"
              placeholder="Search threats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}
            />
          </div>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger data-testid="filter-severity-select" className={`w-full md:w-48 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}>
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200'}>
              <SelectItem value="all">All Severities</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="info">Info</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className={`w-full md:w-48 ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : 'bg-white border-slate-300'}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10 text-white' : 'bg-white border-slate-200'}>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="investigating">Investigating</SelectItem>
              <SelectItem value="mitigated">Mitigated</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Asset-Based View */}
        {viewMode === 'by-asset' ? (
          <div className="space-y-6">
            {Object.keys(threatsByAsset).length === 0 && unassignedThreats.length === 0 ? (
              <div className="text-center py-12">
                <Layers className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                <p className={`font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>No threats found</p>
              </div>
            ) : (
              <>
                {Object.values(threatsByAsset).map(({ asset, threats: assetThreats }) => (
                  <div key={asset.id} className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <Server className={`w-5 h-5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                      <div>
                        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{asset.name}</h3>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>{asset.type} • {asset.hostname || asset.ip_address}</p>
                      </div>
                      <span className={`ml-auto px-2 py-1 rounded text-xs font-medium ${
                        assetThreats.some(t => t.severity === 'critical') ? 'bg-red-500/20 text-red-400' :
                        assetThreats.some(t => t.severity === 'high') ? 'bg-orange-500/20 text-orange-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {assetThreats.length} threat{assetThreats.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {assetThreats.map(threat => (
                        <div key={threat.id} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-white border border-slate-200'}`}>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(threat.severity)}`}>
                              {threat.severity?.toUpperCase()}
                            </span>
                            <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{threat.title}</span>
                          </div>
                          <span className={`text-xs ${getStatusColor(threat.status)}`}>{getStatusLabel(threat.status)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                {/* Unassigned Threats */}
                {unassignedThreats.length > 0 && (
                  <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <AlertTriangle className={`w-5 h-5 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`} />
                      <div>
                        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Unassigned Threats</h3>
                        <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Not linked to any specific asset</p>
                      </div>
                      <span className={`ml-auto px-2 py-1 rounded text-xs font-medium bg-zinc-500/20 text-zinc-400`}>
                        {unassignedThreats.length} threat{unassignedThreats.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {unassignedThreats.map(threat => (
                        <div key={threat.id} className={`flex items-center justify-between p-3 rounded-lg ${isDarkMode ? 'bg-zinc-800/50' : 'bg-white border border-slate-200'}`}>
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(threat.severity)}`}>
                              {threat.severity?.toUpperCase()}
                            </span>
                            <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{threat.title}</span>
                          </div>
                          <span className={`text-xs ${getStatusColor(threat.status)}`}>{getStatusLabel(threat.status)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          /* List View */
          <div className="space-y-4">
            {filteredThreats.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-zinc-600' : 'text-slate-400'}`} />
                <p className={`font-inter ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>No threats found</p>
              </div>
            ) : (
              filteredThreats.map((threat) => (
                <div
                  key={threat.id}
                  data-testid={`threat-item-${threat.id}`}
                  className={`p-5 rounded-xl border transition-all ${isDarkMode ? 'glass-hover border-white/10 hover:border-indigo-500/30' : 'bg-white border-slate-200 hover:border-indigo-300 shadow-sm'}`}
                >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-mono uppercase border ${getSeverityColor(threat.severity)}`}>
                        {threat.severity}
                      </span>
                      <span className="text-zinc-500 text-sm font-inter">{threat.source}</span>
                      {/* Status Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className={`h-6 px-2 ${getStatusColor(threat.status)}`}
                            data-testid={`status-dropdown-${threat.id}`}
                            disabled={updatingStatus === threat.id}
                          >
                            {updatingStatus === threat.id ? (
                              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
                            ) : null}
                            {getStatusLabel(threat.status)}
                            <ChevronDown className="w-3 h-3 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-zinc-900 border-white/10 text-white">
                          {['active', 'investigating', 'mitigated', 'inactive'].map(status => (
                            <DropdownMenuItem 
                              key={status}
                              onClick={() => handleStatusChange(threat.id, status)}
                              className={`cursor-pointer ${getStatusColor(status)} hover:bg-white/10`}
                            >
                              {threat.status === status && <Check className="w-3 h-3 mr-2" />}
                              {getStatusLabel(status)}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <h3 className={`font-rajdhani font-semibold text-xl mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {threat.title}
                    </h3>
                    <p className={`font-inter text-sm ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>{threat.description}</p>
                    <div className={`flex items-center gap-4 mt-4 text-xs font-mono ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Detected: {new Date(threat.detected_at).toLocaleDateString()}
                      </span>
                      {threat.expected_closure && (
                        <span className="flex items-center gap-1 text-yellow-500">
                          <Calendar className="w-3 h-3" />
                          Expected Closure: {new Date(threat.expected_closure).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`w-12 h-12 border rounded-lg flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-red-600/20 border-red-500/30' : 'bg-red-100 border-red-200'}`}>
                    <AlertTriangle className={`w-6 h-6 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
                  </div>
                </div>
              </div>
            ))
          )}
          </div>
        )}
      </div>
    </div>
  );
}
